/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useState } from "react";

import { useTranslation } from "react-i18next";

import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import DialogActions from "@/shared/components/buttons/DialogActions";
import RectangleLoader from "@/shared/components/loaders/RectangleLoader";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import { ROUTES, useMediaQueries } from "../../../../../../../shared";
import TextLink from "../../../../../../TextLink/TextLink";
import { TEXT_LINK_SIZES } from "../../../../../../TextLink/types";
import AccordionDetailsCard from "../../AccordionDetailsCard/AccordionDetailsCard";
import DownloadHistorySingleDay from "../DownloadHistorySingleDay/DownloadHistorySingleDay";
import { useStyles } from "../styles";
import { DownloadHistoryDataDto } from "../types";

interface DownloadHistoryDialogProps {
  vehicleNumber: string;
  downloadHistoryData: DownloadHistoryDataDto[] | undefined;
  isHistoryLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const DownloadHistoryDialog = ({
  vehicleNumber,
  downloadHistoryData,
  isHistoryLoading,
  isOpen,
  onClose
}: DownloadHistoryDialogProps) => {
  const { toMd, fromMd } = useMediaQueries();
  const { t } = useTranslation();

  const [showFullList, setShowFullList] = useState<boolean>(false);

  const { historyEmptyStatePaper } = useStyles({
    fromMd
  });

  const INITIAL_ITEMS_TO_SHOW = 3;
  const HISTORY_LOADER_HEIGHT = "168px";

  const showHistoryDetails =
    !isHistoryLoading && downloadHistoryData && downloadHistoryData.length > 0;

  const hasMoreThanInitialItems =
    downloadHistoryData && downloadHistoryData.length > INITIAL_ITEMS_TO_SHOW;

  const showDownloadHistoryList = (
    downloadHistoryData: DownloadHistoryDataDto[]
  ) =>
    downloadHistoryData?.map(
      (historyObject: DownloadHistoryDataDto, index: number) =>
        showFullList || (!showFullList && index < INITIAL_ITEMS_TO_SHOW) ? (
          <DownloadHistorySingleDay
            key={historyObject.date}
            historyObject={historyObject}
            itemsCount={downloadHistoryData.length}
            index={index}
          />
        ) : null
    );

  const handleTextlinkClick = () => {
    setShowFullList(true);
  };

  const showHistoryEmptyState = !showHistoryDetails && (
    <Paper elevation={0} variant="outlined" css={css(historyEmptyStatePaper)}>
      <Typography variant="body1Medium">
        {t("Download history##no history")}
      </Typography>
    </Paper>
  );

  const showHistoryAccordionList = showHistoryDetails && (
    <>
      <AccordionDetailsCard
        route={ROUTES.Vehicles}
        showTitleArea={false}
        customStyle={{ margin: fromMd ? "16px 0 0 0" : 0 }}
      >
        {showDownloadHistoryList(downloadHistoryData)}
      </AccordionDetailsCard>

      {!showFullList && hasMoreThanInitialItems && (
        <TextLink
          size={TEXT_LINK_SIZES.Small}
          onClick={handleTextlinkClick}
          customStyle={{
            marginTop: "8px"
          }}
        >
          {t("General##show older")}
        </TextLink>
      )}
    </>
  );

  return (
    <Dialog
      data-testid="download-monitoring-history-dialog"
      title={t(`Dialog##download history dialog title`, {
        vehicleNumber
      })}
      open={isOpen}
      close={onClose}
      extendToMaxWidth={toMd}
      isActionsSeparator
      actions={
        <DialogActions
          onClose={onClose}
          isLoading={false}
          hideAcceptButton
          isreadyToConfirm={null}
          cancelButtonText={t("Button##back")}
        />
      }
      isTitleSeparator
      width={DialogWidth.MaxWidth}
    >
      <div data-testid="download-history">
        {isHistoryLoading && (
          <div css={css({ marginTop: "16px" })}>
            {
              <RectangleLoader
                width="100%"
                height={HISTORY_LOADER_HEIGHT}
                testId="download-history-loader"
              />
            }
          </div>
        )}

        {!isHistoryLoading &&
          (showHistoryAccordionList || showHistoryEmptyState)}
      </div>
    </Dialog>
  );
};

export default DownloadHistoryDialog;
